import type { ReactNode } from "react";
import {
  DuviSharedAdminCard as Card,
  DuviSharedAdminCardHeader as CardHeader,
  DuviSharedAdminCardBody as CardBody,
  DuviSharedAdminAvatar as Avatar,
  DuviSharedAdminBadge as Badge,
  DuviSharedAdminStatusPill as StatusPill,
  DuviSharedAdminButton as Button,
  DuviSharedAdminModal as Modal,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminTestIds,
} from "@duvi-agency/ui";
import {
  DuviCoreAdminMonoChip as MonoChip,
  DuviCoreAdminShieldCheck as ShieldCheck,
  DuviCoreAdminShieldOff as ShieldOff,
} from "../../components/duvi-core-admin-kit";
import type { useDuviCoreAdminUserDetail } from "./use-duvi-core-admin-user-detail";
import { duviCoreAdminDetailStatusTone } from "./use-duvi-core-admin-user-detail";

type State = ReturnType<typeof useDuviCoreAdminUserDetail>;

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function ActionRow({
  title,
  desc,
  control,
  danger,
}: {
  title: ReactNode;
  desc: ReactNode;
  control: ReactNode;
  danger?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-border last:border-0">
      <div>
        <div
          className={[
            "font-medium",
            danger ? "text-error" : "text-text-primary",
          ].join(" ")}
        >
          {title}
        </div>
        <div className="text-caption text-text-muted">{desc}</div>
      </div>
      {control}
    </div>
  );
}

export function DuviCoreAdminUserDetailView(state: State) {
  const {
    user,
    isLoading,
    showArchiveModal,
    setShowArchiveModal,
    forceResetLoading,
    disableLoading,
    enableLoading,
    archiveLoading,
    handleForceReset,
    handleDisable,
    handleEnable,
    handleArchive,
  } = state;

  if (isLoading || !user) {
    return (
      <div data-testid={DuviSharedAdminTestIds.userDetailPage}>
        <div className="py-16 text-center text-body-sm text-text-muted">
          Loading user…
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={DuviSharedAdminTestIds.userDetailPage}
      className="grid grid-cols-[40%_60%] gap-5 items-start max-[900px]:grid-cols-1"
    >
      <Card pad>
        <div className="flex flex-col items-center text-center gap-3">
          <Avatar name={user.name} src={user.avatarUrl} size="lg" />
          <div>
            <div className="text-h3 font-bold">{user.name}</div>
            <div className="text-text-muted text-body-sm mt-0.5">
              {user.email}
            </div>
          </div>
          <StatusPill status={duviCoreAdminDetailStatusTone(user.status)}>
            {cap(user.status)}
          </StatusPill>
          <div className="text-text-muted text-caption">
            Created {user.createdAt}
          </div>
          <MonoChip>{user.id}</MonoChip>
        </div>
      </Card>

      <div className="flex flex-col gap-5">
        <Card>
          <CardHeader
            title="Security"
            icon={<Icon name="security" size={18} />}
          />
          <CardBody>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span
                  className={[
                    "inline-flex",
                    user.mfaEnabled ? "text-success" : "text-text-muted",
                  ].join(" ")}
                >
                  {user.mfaEnabled ? (
                    <ShieldCheck size={20} />
                  ) : (
                    <ShieldOff size={20} />
                  )}
                </span>
                <div>
                  <div className="font-medium">Two-factor authentication</div>
                  <div className="text-caption text-text-muted">
                    Protects sign-in with a second factor
                  </div>
                </div>
              </div>
              <Badge variant={user.mfaEnabled ? "success" : "neutral"}>
                {user.mfaEnabled ? "Enabled" : "Not set up"}
              </Badge>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Account actions"
            icon={<Icon name="settings" size={18} />}
          />
          <CardBody>
            <div className="flex flex-col gap-3">
              <ActionRow
                title="Force reset password"
                desc="Require a new password at next sign-in."
                control={
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={forceResetLoading}
                    onClick={handleForceReset}
                  >
                    {forceResetLoading ? "Resetting…" : "Reset"}
                  </Button>
                }
              />
              <ActionRow
                title={
                  user.status === "disabled" ? "Enable user" : "Disable user"
                }
                desc="Controls whether this user can sign in."
                control={
                  user.status === "disabled" ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={enableLoading}
                      onClick={handleEnable}
                    >
                      {enableLoading ? "Enabling…" : "Enable"}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={disableLoading}
                      onClick={handleDisable}
                    >
                      {disableLoading ? "Disabling…" : "Disable"}
                    </Button>
                  )
                }
              />
              <ActionRow
                title="Archive user"
                desc="Remove from active lists. Reversible."
                danger
                control={
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={user.status === "archived"}
                    onClick={() => setShowArchiveModal(true)}
                  >
                    {user.status === "archived" ? "Archived" : "Archive"}
                  </Button>
                }
              />
            </div>
          </CardBody>
        </Card>
      </div>

      {showArchiveModal && (
        // display:contents -- DuviSharedAdminModal renders `position: fixed`,
        // which takes it out of this wrapper's normal flow. Without `contents`
        // the wrapper collapses to 0x0 and Playwright reports the testid
        // "hidden" even though the modal is fully visible (STY-906 triage).
        <div
          className="contents"
          data-testid={DuviSharedAdminTestIds.archiveUserDialog}
        >
          <Modal
            title={`Archive ${user.name}?`}
            onClose={() => setShowArchiveModal(false)}
            description={`${user.name} will be moved to the archive and hidden from active lists. You can unarchive them later.`}
            footer={
              <>
                <Button
                  variant="secondary"
                  onClick={() => setShowArchiveModal(false)}
                  data-testid={DuviSharedAdminTestIds.archiveUserDialogCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={archiveLoading}
                  onClick={handleArchive}
                  data-testid={DuviSharedAdminTestIds.archiveUserDialogConfirm}
                >
                  {archiveLoading ? "Archiving…" : "Archive user"}
                </Button>
              </>
            }
          />
        </div>
      )}
    </div>
  );
}
